import React, { useState } from "react";
import styled from "styled-components";
import { Container } from "react-bootstrap";
import { useAuth } from "../../context/AuthContext";
import { awardXp } from "../../lib/gamification";

const Section = styled.section`
  padding: 48px 0;
  background: var(--nd-black);
`;

const Kicker = styled.p`
  font-family: 'Space Mono', monospace;
  font-size: 11px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--nd-text-secondary);
  margin: 0 0 12px;
`;

const Heading = styled.h2`
  font-family: 'Space Grotesk', sans-serif;
  font-size: 24px;
  font-weight: 500;
  color: var(--nd-text-display);
  margin: 0 0 24px;
`;

const List = styled.ol`
  list-style: none;
  padding: 0;
  margin: 0;
  border-top: 1px solid var(--nd-border);
`;

const Row = styled.li`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 14px 0;
  border-bottom: 1px solid var(--nd-border);
`;

const RowIndex = styled.span`
  font-family: 'Space Mono', monospace;
  font-size: 11px;
  color: var(--nd-text-disabled);
  min-width: 28px;
`;

const RowTitle = styled.span`
  font-family: 'Space Grotesk', sans-serif;
  font-size: 15px;
  color: var(--nd-text-primary);
  flex: 1;
  line-height: 1.4;
`;

const RowDuration = styled.span`
  font-family: 'Space Mono', monospace;
  font-size: 12px;
  color: var(--nd-text-secondary);
  min-width: 56px;
  text-align: right;
`;

const FreeBadge = styled.span`
  font-family: 'Space Mono', monospace;
  font-size: 10px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--nd-success);
  border: 1px solid var(--nd-success);
  padding: 2px 6px;
  border-radius: 4px;
`;

const MarkBtn = styled.button`
  font-family: 'Space Mono', monospace;
  font-size: 12px;
  background: transparent;
  border: 1px solid var(--nd-border-visible);
  color: ${({ done }) => done ? "var(--nd-text-display)" : "var(--nd-text-disabled)"};
  width: 24px;
  height: 24px;
  border-radius: 4px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  &:hover { border-color: var(--nd-text-display); }
`;

const Toggle = styled.button`
  font-family: 'Space Mono', monospace;
  font-size: 12px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--nd-text-primary);
  background: transparent;
  border: 1px solid var(--nd-border-visible);
  border-radius: 999px;
  padding: 10px 18px;
  margin-top: 16px;
  min-height: 44px;
  cursor: pointer;
  transition: border-color 250ms cubic-bezier(0.25, 0.1, 0.25, 1);
  &:hover { border-color: var(--nd-text-primary); }
`;

const formatDuration = (min) => {
  const m = Math.floor(min);
  const s = Math.round((min - m) * 60);
  return `${m}:${String(s).padStart(2, "0")}`;
};

const COLLAPSED_COUNT = 4;

const LessonList = ({ lessons, moduleId }) => {
  const [expanded, setExpanded] = useState(false);
  const [doneIds, setDoneIds] = useState(new Set());
  const { user } = useAuth();
  const toggleDone = (lessonId) => {
    setDoneIds((prev) => {
      const next = new Set(prev);
      if (next.has(lessonId)) {
        next.delete(lessonId);
        return next;
      }
      next.add(lessonId);
      if (user) {
        awardXp({ event: "lesson.completed", payload: { lessonId } });
        // Fire module-completed when the last lesson flips to done.
        // Server dedup (idempotencyKey=moduleId) ensures repeat fires
        // from refreshing the page or re-checking are no-ops.
        if (moduleId && next.size === lessons.length) {
          awardXp({
            event: "course.module_completed",
            payload: { moduleId },
          });
        }
      }
      return next;
    });
  };
  const visible = expanded ? lessons : lessons.slice(0, COLLAPSED_COUNT);
  const hidden = lessons.length - COLLAPSED_COUNT;
  return (
    <Section>
      <Container>
        <Kicker>CURRICULUM</Kicker>
        <Heading>{lessons.length} lessons</Heading>
        <List>
          {visible.map((l, i) => (
            <Row key={l.id}>
              <MarkBtn
                done={doneIds.has(l.id)}
                onClick={() => toggleDone(l.id)}
                aria-label={doneIds.has(l.id) ? "Mark incomplete" : "Mark complete"}
              >
                {doneIds.has(l.id) ? "✓" : ""}
              </MarkBtn>
              <RowIndex>{String(i + 1).padStart(2, "0")}</RowIndex>
              <RowTitle>{l.title}</RowTitle>
              {l.free && <FreeBadge>Free</FreeBadge>}
              <RowDuration>{formatDuration(l.durationMin)}</RowDuration>
            </Row>
          ))}
        </List>
        {hidden > 0 && (
          <Toggle
            type="button"
            aria-expanded={expanded}
            onClick={() => setExpanded((x) => !x)}
          >
            {expanded ? "Collapse" : `Show all ${lessons.length} lessons`}
          </Toggle>
        )}
      </Container>
    </Section>
  );
};

export default LessonList;
